import { useLanguage } from "@tlc/ls2pac/components/Language";
import { EResourceCheckoutResponse } from "./DownloadableCheckoutDialog";
import { useStationLocation } from "@tlc/ls2pac/components/StationLocation";
import { useLocationConfiguration } from "@tlc/ls2pac/components/LocationConfiguration";
import { useMemo } from "react";
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

interface DownloadableCheckoutSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  onClickDownload: () => void;
  response: EResourceCheckoutResponse;
}
export const DownloadableCheckoutSuccessDialog = ({
  open,
  onClose,
  onClickDownload,
  response,
}: DownloadableCheckoutSuccessDialogProps) => {
  const { translate } = useLanguage();
  const { stationLocation } = useStationLocation();
  const { locationConfiguration } = useLocationConfiguration();
  const { overrideDownloadNowStationLocation } =
    locationConfiguration?.pacSettings || {};

  const formattedDate = useMemo(() => {
    if (!open || !response?.expirationDate) {
      return null;
    }

    return new Intl.DateTimeFormat(navigator.language).format(
      new Date(response.expirationDate)
    );
  }, [translate, open, response]);

  const downloadNowBlocked = useMemo(
    () => !!stationLocation && !overrideDownloadNowStationLocation,
    [stationLocation, overrideDownloadNowStationLocation]
  );

  const getMessage = () => {
    if (downloadNowBlocked) {
      return (
        <>
          <Typography>
            {/* Due to the library's settings you cannot download this eResource on computers inside the network. */}
            {translate("eResourceStationLocationMessage")}
          </Typography>
          <Typography>
            {/* stationLocationCheckoutLaterEResourceSuccessMessage=You will be able to download later from Account Activity > Loans. */}
            {translate("stationLocationCheckoutLaterEResourceSuccessMessage")}
          </Typography>
        </>
      );
    }

    return (
      <Typography>
        {/* If you select Download Later, go to Account Activity > Loans when you are ready to download. */}
        {translate("checkoutLaterEResourceSuccessMessage")}
      </Typography>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{translate("checkoutEResourceDialogTitle")}</DialogTitle>
      <DialogContent>
        <Stack gap={1}>
          <Typography>
            {translate("checkoutEResourceDateMessage")} {formattedDate}.
          </Typography>
          {getMessage()}
        </Stack>
      </DialogContent>
      <DialogActions>
        {downloadNowBlocked ? (
          <Button className="close" onClick={onClose}>
            {translate("ok")}
          </Button>
        ) : (
          <>
            <Button variant={"outlined"} onClick={onClose}>
              {translate("eResourceCheckoutDownloadLaterButton")}
            </Button>
            <Button variant={"contained"} onClick={onClickDownload}>
              {translate("eResourceCheckoutDownloadNowButton")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
